import { useOuterClick } from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import styles from "./ToolboxMenu.module.css";

export interface ToolboxMenuTriggerState {
  open: boolean;
  toggle: () => void;
}

export interface ToolboxMenuProps {
  trigger: React.ReactNode | ((state: ToolboxMenuTriggerState) => React.ReactNode);
  children: React.ReactNode;
  closeOnSelect?: boolean;
  /**
   * When true, inner clicks (e.g. selecting an item) never close the menu —
   * only an outside click or the trigger closes it. Overrides `closeOnSelect`.
   */
  persistent?: boolean;
  direction?: "up" | "down";
  align?: "center" | "start" | "end";
  className?: string;
  style?: React.CSSProperties;
}

const ALIGN_CLASS = {
  center: "alignCenter",
  start: "alignStart",
  end: "alignEnd",
} as const;

const DIRECTION_CLASS = {
  up: "directionUp",
  down: "directionDown",
} as const;

// Per-item delay so items appear staggered ("fan out"). Opening starts from the
// item closest to the trigger; closing reverses, so they retract toward it.
const OPEN_STAGGER_MS = 45;
const CLOSE_STAGGER_MS = 30;

export const ToolboxMenu = ({
  trigger,
  children,
  closeOnSelect = false,
  persistent = false,
  direction = "up",
  align = "center",
  className,
  style,
}: ToolboxMenuProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useOuterClick(containerRef, () => setOpen(false));

  // Anchor the items via position: fixed so they escape any `overflow: clip`
  // on ancestor elements (e.g. TopBar's right slot, which would otherwise
  // crop items that grow wider than the trigger — like the RaiseHand button
  // when it expands to show its "Requête envoyée" label).
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect());
      }
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  const itemsStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!triggerRect) return undefined;
    const s: React.CSSProperties = { position: "fixed" };
    if (direction === "up") {
      s.bottom = window.innerHeight - triggerRect.top;
      s.top = "auto";
    } else {
      s.top = triggerRect.bottom;
      s.bottom = "auto";
    }
    if (align === "end") {
      s.right = window.innerWidth - triggerRect.right;
      s.left = "auto";
    } else if (align === "start") {
      s.left = triggerRect.left;
      s.right = "auto";
    } else {
      // Center: place left at the trigger's center; .alignCenter's
      // translateX(-50%) handles the actual centering.
      s.left = triggerRect.left + triggerRect.width / 2;
      s.right = "auto";
    }
    return s;
  }, [triggerRect, direction, align]);

  const toggle = () => setOpen((v) => !v);
  const isRenderTrigger = typeof trigger === "function";

  const items = React.Children.toArray(children).filter(Boolean);
  const total = items.length;
  const shouldCloseOnSelect = closeOnSelect && !persistent;

  return (
    <div ref={containerRef} className={clsx(styles.container, className)} style={style}>
      <div
        className={clsx(
          styles.items,
          styles[DIRECTION_CLASS[direction]],
          styles[ALIGN_CLASS[align]],
          open && styles.open,
        )}
        style={itemsStyle}
        onClick={shouldCloseOnSelect ? () => setOpen(false) : undefined}
      >
        {items.map((item, i) => {
          const delay = open ? i * OPEN_STAGGER_MS : (total - 1 - i) * CLOSE_STAGGER_MS;
          return (
            <div
              key={i}
              className={styles.item}
              style={{ transitionDelay: `${delay}ms` }}
            >
              {item}
            </div>
          );
        })}
      </div>
      <div ref={triggerRef} className={styles.trigger} onClick={isRenderTrigger ? undefined : toggle}>
        {isRenderTrigger
          ? (trigger as (state: ToolboxMenuTriggerState) => React.ReactNode)({ open, toggle })
          : trigger}
      </div>
    </div>
  );
};
